import { yupResolver } from '@hookform/resolvers/yup'
import type { ShiftRQSchema } from '@libs/data-access/queries'
import moment from 'moment'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'

import type { MealValue } from '../../../ShiftForm/components/MealRow/MealRow'
import type { UnplannedFormData } from '../../models'
import { getBreaksForForm } from '../../utils/getBreaksForForm'
import { useRealHoursSchemaValidations } from '../../validations'

type UnplannedShiftRealHoursForm = {
  shift?: ShiftRQSchema
  breakAsReal: boolean
  padType: string
}

const useUnplannedShiftRealHoursForm = ({
  shift,
  breakAsReal,
  padType
}: UnplannedShiftRealHoursForm) => {
  const unplannedShiftRealHoursSchemaValidations =
    useRealHoursSchemaValidations()
  const hasEmployeeModifyReal = Boolean(
    shift?.real_hours_last_modified_by_employee_at
  )
  const momentRealStart = moment(shift?.real_starts_at)
  const momentRealEnd = moment(shift?.real_ends_at)

  const getRealMeals = useMemo(() => {
    return Number(shift?.shift_meals?.length) > 0
      ? shift?.shift_meals.map(shiftMeal => {
          const mealNotBeenTaken = shiftMeal.real_meal_type === null

          return {
            value: {
              id: shiftMeal.id || null,
              checked: !mealNotBeenTaken,
              initialMeal: shiftMeal.real_meal_type
                ? shiftMeal.real_meal_type
                : shiftMeal.planned_meal_type,
              meal: {
                value: shiftMeal.real_meal_type
                  ? shiftMeal.real_meal_type
                  : shiftMeal?.planned_meal_type
              }
            }
          } as MealValue
        })
      : []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const shiftEvents = shift?.shift_events

  const clockInEvent =
    shiftEvents && shiftEvents.find(event => event?.event_type === 'clock_in')
  const clockOutEvent =
    shiftEvents && shiftEvents.find(event => event?.event_type === 'clock_out')

  const shiftIsFinishedWithoutOut =
    !clockOutEvent && moment(shift?.ends_at) < moment(new Date())

  const isShiftUnvalidate = Boolean(shift?.validator_id)
  const defaultValues = {
    breaks: getBreaksForForm(
      shift?.shift_breaks,
      true,
      breakAsReal,
      shiftIsFinishedWithoutOut,
      hasEmployeeModifyReal,
      Boolean(clockInEvent),
      isShiftUnvalidate,
      padType
    ).filter(Boolean) as any,
    meals: getRealMeals,
    label: { value: shift?.label_id || '' },
    shift_time_range: [
      momentRealStart.format('HH:mm'),
      momentRealEnd.format('HH:mm')
    ],
    real_note: shift?.real_note || ''
  }

  const useFormMethods = useForm<UnplannedFormData>({
    mode: 'onBlur',
    defaultValues,
    resolver: yupResolver(unplannedShiftRealHoursSchemaValidations)
  })

  return useFormMethods
}

export default useUnplannedShiftRealHoursForm
